import { useState, useEffect, useRef } from 'react';
import {
  Grid,
  GridItem,
  Box,
  VStack,
  Text,
  Avatar,
  HStack,
  Icon,
  Divider,
  Button,
  useToast,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import Navigation from '../Navigation/Navigation';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { FiArrowLeft } from 'react-icons/fi';
import { motion, useInView } from 'framer-motion';
import '../../styles/fonts.css';
import './Explore.css';

import { backendURL } from '../../App';

const MotionBox = motion(Box);

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </MotionBox>
  );
};

const StorySidebar = ({ story, chapters, activeChapter }) => {
  const navigate = useNavigate();

  const handleAuthorClick = (author) => {
    navigate(`/author/${encodeURIComponent(author)}`);
  };

  return (
    <Box
      position="relative"
      height="100vh"
      display="flex"
      flexDirection="column"
      color="#1A1A1A"
    >
      {/* Back to explore */}
      <Box px={4} pt={4} pb={2}>
        <Flex
          as="button"
          align="center"
          gap={1.5}
          fontSize="xs"
          color="#A1A1AA"
          fontWeight="500"
          _hover={{ color: '#E05252' }}
          transition="color 0.2s"
          onClick={() => navigate('/explore')}
          cursor="pointer"
        >
          <Icon as={FiArrowLeft} boxSize={3.5} />
          Explore
        </Flex>
      </Box>

      {/* Author info */}
      <Box px={4} pt={1} pb={3}>
        <HStack
          spacing={3}
          align="center"
          onClick={() => handleAuthorClick(story.author)}
          cursor="pointer"
          _hover={{ opacity: 0.7 }}
          transition="opacity 0.2s"
          mb={1}
        >
          <Avatar size="sm" name={story.author} bg="#E05252" color="white" />
          <VStack spacing={0} align="start">
            <Text
              fontWeight="600"
              fontSize="sm"
              color="#1A1A1A"
            >
              {story.author}
            </Text>
            <Text fontSize="xs" color="#A1A1AA">{story.published_date}</Text>
          </VStack>
        </HStack>
      </Box>
      <Divider borderColor="#E4E4E7" mx={4} w="auto" />

      {/* Chapter label */}
      <Box px={4} pt={3} pb={1}>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="#A1A1AA"
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Chapters
        </Text>
      </Box>

      {/* Scrollable chapters section */}
      <Box
        flex="1"
        mb={6}
        css={{
          overflowY: 'scroll',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
            background: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.12)',
            borderRadius: '3px',
            minHeight: '20px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 0, 0, 0.2)',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.12) transparent',
        }}
        style={{
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}
      >
        <VStack spacing={0.5} px={3} py={1} align="stretch">
          {chapters.map((chapter, index) => (
            <div
              key={chapter.id}
              className="sidebar-chapter-item"
              style={{
                background: activeChapter === chapter.id ? '#F5F3F0' : 'transparent',
                color: activeChapter === chapter.id ? '#1A1A1A' : undefined,
              }}
              onClick={() => document.getElementById(chapter.id)?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="chapter-number">{index + 1}</span>
              <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                {chapter.title}
              </Text>
            </div>
          ))}
        </VStack>
      </Box>

      {/* Fixed bottom section */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        p={4}
        bg="white"
        borderTop="1px solid"
        borderColor="#E4E4E7"
      >
        <Button
          as="a"
          href="mailto:support@writewizard.org"
          variant="ghost"
          size="sm"
          width="100%"
          color="#A1A1AA"
          fontWeight="500"
          fontSize="sm"
          _hover={{
            bg: '#FEF2F2',
            color: '#52525B'
          }}
          transition="all 0.2s"
        >
          Contact
        </Button>
      </Box>
    </Box>
  );
};

const StoryView = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [story, setStory] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const { onLogOut, isAuthenticated, authenticatedFetch } = useOutletContext();

  // Track active chapter on scroll
  useEffect(() => {
    if (!story) return;

    const handleScroll = () => {
      const chapters = story.chapters;
      for (let i = chapters.length - 1; i >= 0; i--) {
        const el = document.getElementById(chapters[i].id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200) {
            setActiveChapter(chapters[i].id);
            return;
          }
        }
      }
      if (chapters.length > 0) {
        setActiveChapter(chapters[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [story]);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const response = await fetch(backendURL + `/explore/story/${storyId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          setError('');
          return;
        }

        const data = await response.json();
        setStory(data);
        setLikesCount(data.likes);
      } catch (error) {
        setError('');
        return;
      } finally {
        setIsLoading(false);
      }
    };

    if (storyId) {
      fetchStory();
    }
  }, [storyId]);

  useEffect(() => {
    const checkLikeStatus = async () => {
      try {
        const response = await fetch(backendURL + `/explore/check_story_like/${storyId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (data.success) {
          setIsLiked(data.isLiked);
        }
      } catch (error) {
        return;
      }
    };

    if (story && isAuthenticated) {
      checkLikeStatus();
    }
  }, [storyId, toast, story, isAuthenticated]);

  const handleLikeClick = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setLikesCount((prev) => isLiked ? prev - 1 : prev + 1);
    setIsLiked((prev) => !prev);
    try {
      const response = await authenticatedFetch(backendURL + `/explore/like_story/${storyId}`, {
        method: 'POST',
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        console.log('response')
        const errorText = await response.text();
        console.error('Story fetch failed:', response.status, errorText);
        setError(`Failed to load story: ${response.status} - ${errorText}`);
        return;
      }
    } catch (error) {
      console.log('error')
      console.log(error)
       return;
    }
  };

  if (isLoading) {
    return (
      <Box bg="#F5F3F0" minH="100vh">
        {isAuthenticated && (
          <Navigation
            bg="#F5F3F0"
            onRouteChange={(route) => navigate(`/${route}`)}
            onLogOut={onLogOut}
            isAuthenticated={isAuthenticated}
          />
        )}
        <VStack spacing={8} justify="center" align="center" h="50vh">
          <Spinner size="xl" color="#E05252" thickness="3px" />
          <Text color="#52525B" fontSize="lg">Loading story...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !story) {
    return (
      <Box bg="#F5F3F0" minH="100vh">
        {isAuthenticated && (
          <Navigation
            bg="#F5F3F0"
            onRouteChange={(route) => navigate(`/${route}`)}
            onLogOut={onLogOut}
            isAuthenticated={isAuthenticated}
          />
        )}
        <div className="error">
          {error || 'Story not found'}
        </div>
      </Box>
    );
  }

  return (
    <Box bg="#F5F3F0" minH="100vh">
      <Grid templateColumns="repeat(48, 1fr)" bg="#F5F3F0">
        <GridItem
          as="aside"
          colSpan="7"
          minHeight="100vh"
          position="sticky"
          top="0"
          alignSelf="start"
          minW="125px"
          bg="white"
          borderRight="1px solid"
          borderColor="#E4E4E7"
          pt="20px"
          display={{ base: "none", md: "block" }}
        >
          <StorySidebar story={story} chapters={story.chapters} activeChapter={activeChapter} />
        </GridItem>

        <GridItem
          as="main"
          colSpan={{ base: 48, md: 41 }}
        >
          {isAuthenticated && (
            <Navigation
              bg="#F5F3F0"
              onRouteChange={(route) => navigate(`/${route}`)}
              onLogOut={onLogOut}
              isAuthenticated={isAuthenticated}
            />
          )}
          <div className="story-view-content">
            {/* Title with animation */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h1 className="story-view-title">{story.title}</h1>

              {/* Meta on mobile (sidebar hidden) */}
              <Box display={{ base: "block", md: "none" }} mb={6}>
                <div className="story-view-meta">
                  <Avatar size="sm" name={story.author} bg="#E05252" color="white" />
                  <VStack spacing={0} align="start">
                    <span
                      className="author-name"
                      onClick={() => navigate(`/author/${encodeURIComponent(story.author)}`)}
                    >
                      {story.author}
                    </span>
                    <span className="publish-date">{story.published_date}</span>
                  </VStack>
                </div>
              </Box>

              {/* Decorative divider on desktop */}
              <Box display={{ base: "none", md: "block" }} mb={8}>
                <Box h="1px" bg="#E4E4E7" />
              </Box>
            </MotionBox>

            {/* Chapters */}
            <VStack align="stretch" spacing={10}>
              {story.chapters.map((chapter, index) => (
                <FadeInSection key={chapter.id} delay={Math.min(index * 0.08, 0.3)}>
                  <div className="chapter-section" id={chapter.id}>
                    <h2 className="chapter-title">{chapter.title}</h2>
                    <div className="chapter-content">
                      {chapter.content}
                    </div>
                  </div>
                </FadeInSection>
              ))}
            </VStack>

            {/* Like section */}
            <FadeInSection>
              <div className="like-section">
                <HStack spacing={3} alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Icon
                      as={isLiked ? FaHeart : FaRegHeart}
                      color={isLiked ? "#E05252" : "#A1A1AA"}
                      boxSize={5}
                      transition="color 0.2s, transform 0.2s"
                      _hover={{ transform: 'scale(1.15)' }}
                      cursor="pointer"
                      onClick={handleLikeClick}
                    />
                    <Text color="#52525B" ml={2} fontSize="md" fontWeight="500">{likesCount}</Text>
                  </Box>
                  <Button
                    leftIcon={<Icon as={isLiked ? FaHeart : FaRegHeart} />}
                    bg={isLiked ? "#E05252" : "transparent"}
                    color={isLiked ? "white" : "#52525B"}
                    borderColor={isLiked ? "#E05252" : "#E4E4E7"}
                    border="1px solid"
                    variant={isLiked ? "solid" : "outline"}
                    size="sm"
                    borderRadius="full"
                    onClick={handleLikeClick}
                    fontWeight="500"
                    px={5}
                    _hover={{
                      bg: isLiked ? '#C94545' : '#FEF2F2',
                      borderColor: isLiked ? '#C94545' : '#E05252',
                      color: isLiked ? 'white' : '#E05252',
                    }}
                    transition="all 0.25s"
                  >
                    {isLiked ? 'Liked' : 'Like this story'}
                  </Button>
                </HStack>
              </div>
            </FadeInSection>

            {/* Bottom spacer */}
            <Box h={16} />
          </div>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default StoryView;
